<div class="design-hero">
  <h1 class="page-title">Design System</h1>
  <p class="page-subtitle">
    A comprehensive, living reference guide that documents and standardizes the foundational design elements—including typography
    scales, color palettes, surface treatments, and elevation hierarchies—used consistently throughout the entire admin experience.
    This system serves as the single source of truth for designers and developers, ensuring that every UI decision remains visually
    cohesive, accessible, and maintainable while enabling the interface to scale elegantly as new features and components are
    introduced over time.
  </p>
</div>

<section a="surface" class="design-section">
  <h2 class="section-title">Surface</h2>
  <p class="section-description">Apply these tokens to define background layers, elevations, and interactive states.</p>
  <div class="surface-grid">
    @for (surface of surfaces; track surface.variable) {
    <div class="surface-card">
      <div class="surface-card__swatch" [style.background]="`var(${surface.variable})`"></div>
      <div class="surface-card__meta">
        <p class="surface-card__name">{{ surface.name }}</p>
        <p class="surface-card__token">{{ surface.variable }}</p>
        <p class="surface-card__description">{{ surface.description }}</p>
      </div>
    </div>
    }
  </div>
</section>

<section a="elevation" class="design-section">
  <h2 class="section-title">Elevation</h2>
  <p class="section-description">Use elevation tokens to communicate depth and hierarchy.</p>
  <div class="elevation-grid">
    @for (elevation of elevations; track elevation.variable) {
    <div class="elevation-card" [style.boxShadow]="'var(' + elevation.variable + ')'">
      <div class="elevation-card__header">
        <p class="elevation-card__name">{{ elevation.name }}</p>
        <p class="elevation-card__token">{{ elevation.variable }}</p>
      </div>
      <p class="elevation-card__description">{{ elevation.description }}</p>
    </div>
    }
  </div>
</section>

<section a="color-pallete" class="design-section">
  <h2 class="section-title">Color Pallete</h2>
  <p class="section-description">
    Use these palette tokens to apply semantic color roles and maintain a consistent look across material surfaces.
  </p>
  <div class="palette-grid">
    @for (section of sections; track section.title) {
    <div class="palette-column">
      <div class="swatch-column">
        @for (swatch of section.swatches; track swatch.variable) {
        <div class="teal-card" [style.background]="`var(${swatch.variable})`">
          <span class="teal-card__label">{{ swatch.name }}</span>
          <span class="teal-card__token">{{ swatch.variable }}</span>
          <span class="teal-card__hex">{{ swatch.hex }}</span>
        </div>
        }
      </div>
    </div>
    }
  </div>
</section>

<section a="typography" class="design-section typography-section">
  <h2 class="section-title">Typography</h2>
  <p class="section-description">
    Reference the typography tokens below to keep headings, body copy, and system labels consistent across the admin UI.
  </p>
  <div class="typography-group">
    <h3 class="typography-title">Font size</h3>
    <div class="typography-list">
      @for (sample of fontSizes; track sample.variable) {
      <div class="typography-row">
        <span class="typo-variable">{{ sample.variable }}</span>
        <p class="typo-sample typo-sample--font-size" [style.fontSize]="'var(' + sample.variable + ')'">{{ sample.description }}</p>
      </div>
      }
    </div>
  </div>

  <div class="typography-group">
    <h3 class="typography-title">Font weight</h3>
    <div class="typography-list">
      @for (sample of fontWeights; track sample.variable) {
      <div class="typography-row">
        <span class="typo-variable">{{ sample.variable }}</span>
        <p class="typo-sample" [style.fontWeight]="'var(' + sample.fontWeightVariable + ')'">{{ sample.description }}</p>
      </div>
      }
    </div>
  </div>

  <div class="typography-group">
    <h3 class="typography-title">Line height</h3>
    <div class="typography-list">
      @for (sample of lineHeights; track sample.variable) {
      <div class="typography-row">
        <span class="typo-variable">{{ sample.variable }}</span>
        <p class="typo-sample" [style.lineHeight]="'var(' + sample.lineHeightVariable + ')'">{{ sample.description }}</p>
      </div>
      }
    </div>
  </div>

  <div class="typography-group">
    <h3 class="typography-title">Letter spacing</h3>
    <div class="typography-list">
      @for (sample of letterSpacings; track sample.variable) {
      <div class="typography-row">
        <span class="typo-variable">{{ sample.variable }}</span>
        <p class="typo-sample" [style.letterSpacing]="'var(' + sample.letterSpacingVariable + ')'">{{ sample.description }}</p>
      </div>
      }
    </div>
  </div>
</section>

<section a="iconography" class="design-section design-icons">
  <h2 class="section-title">Iconography</h2>
  <p class="section-description">A glyph-based material symbol icon set used for consistent system actions and navigation cues.</p>
  <div class="design-icons__grid">
    @for (icon of iconSamples; track icon.name) {
    <div class="design-icons__card">
      <ng-glyph class="design-icons__glyph" [name]="icon.name"></ng-glyph>
      <div class="design-icons__meta">
        <span class="design-icons__label">{{ icon.label }}</span>
        <span class="design-icons__name">{{ icon.name }}</span>
      </div>
    </div>
    }
  </div>
</section>

<section a="avatar" class="design-section design-avatars">
  <h2 class="section-title">Avatar</h2>
  <p class="section-description">Round avatar component with size variants and initials fallback when no image is provided.</p>
  <div class="design-avatars__grid">
    <div class="design-avatars__card">
      <app-avatar name="John Doe" size="sm"></app-avatar>
      <div class="design-avatars__meta">
        <span class="design-avatars__label">Small</span>
        <span class="design-avatars__token">sm</span>
      </div>
    </div>
    <div class="design-avatars__card">
      <app-avatar name="John Doe"></app-avatar>
      <div class="design-avatars__meta">
        <span class="design-avatars__label">Medium</span>
        <span class="design-avatars__token">md (default)</span>
      </div>
    </div>
    <div class="design-avatars__card">
      <app-avatar name="John Doe" size="lg"></app-avatar>
      <div class="design-avatars__meta">
        <span class="design-avatars__label">Large</span>
        <span class="design-avatars__token">lg</span>
      </div>
    </div>
  </div>
</section>

<section a="buttons" class="design-section design-buttons">
  <h2 class="section-title">Buttons</h2>
  <p class="section-description">Use button variants to communicate hierarchy and keep actions visually consistent.</p>
  <div class="design-buttons__grid">
    <div class="design-buttons__card">
      <p class="design-buttons__label">Primary</p>
      <p class="design-buttons__hint">Use for the main action on a screen.</p>
      <div class="design-buttons__row">
        <app-button variant="primary">Primary</app-button>
        <app-button variant="primary" [disabled]="true">Primary disabled</app-button>
      </div>
    </div>
    <div class="design-buttons__card">
      <p class="design-buttons__label">Secondary</p>
      <p class="design-buttons__hint">Use for supporting or alternate actions.</p>
      <div class="design-buttons__row">
        <app-button variant="secondary">Secondary</app-button>
        <app-button variant="secondary" [disabled]="true">Secondary disabled</app-button>
      </div>
    </div>
    <div class="design-buttons__card">
      <p class="design-buttons__label">Ghost</p>
      <p class="design-buttons__hint">Use for low-emphasis actions or inline controls.</p>
      <div class="design-buttons__row">
        <app-button variant="ghost">Ghost</app-button>
        <app-button variant="ghost" [disabled]="true">Ghost disabled</app-button>
      </div>
    </div>
    <div class="design-buttons__card">
      <p class="design-buttons__label">Simple</p>
      <p class="design-buttons__hint">Use for minimal actions with no padding.</p>
      <div class="design-buttons__row">
        <app-button variant="simple">Simple</app-button>
        <app-button variant="simple" [disabled]="true">Simple disabled</app-button>
      </div>
    </div>
  </div>
</section>

<section a="modals" class="design-section design-modals">
  <h2 class="section-title">Modals</h2>
  <p class="section-description">Preview confirm modal variants and their visual hierarchy.</p>
  <div class="design-buttons__grid">
    <div class="design-buttons__card">
      <p class="design-buttons__label">Primary confirm</p>
      <p class="design-buttons__hint">Use for standard confirmations.</p>
      <div class="design-buttons__row">
        <app-button variant="primary" type="button" (click)="openConfirmModal('primary')">Show modal</app-button>
      </div>
    </div>
    <div class="design-buttons__card">
      <p class="design-buttons__label">Danger confirm</p>
      <p class="design-buttons__hint">Use for destructive actions.</p>
      <div class="design-buttons__row">
        <app-button variant="danger" type="button" (click)="openConfirmModal('danger')">Show modal</app-button>
      </div>
    </div>
  </div>
</section>

<section a="toasts" class="design-section design-toasts">
  <h2 class="section-title">Toasts</h2>
  <p class="section-description">Trigger each toast variant to review messaging hierarchy and color.</p>
  <div class="design-buttons__grid">
    <div class="design-buttons__card">
      <p class="design-buttons__label">Success</p>
      <p class="design-buttons__hint">Use when an action completes successfully.</p>
      <div class="design-buttons__row">
        <app-button variant="primary" type="button" (click)="showToast('success')">Show success</app-button>
      </div>
    </div>
    <div class="design-buttons__card">
      <p class="design-buttons__label">Error</p>
      <p class="design-buttons__hint">Use when an action fails or needs attention.</p>
      <div class="design-buttons__row">
        <app-button variant="danger" type="button" (click)="showToast('error')">Show error</app-button>
      </div>
    </div>
    <div class="design-buttons__card">
      <p class="design-buttons__label">Warning</p>
      <p class="design-buttons__hint">Use for cautionary or potentially risky actions.</p>
      <div class="design-buttons__row">
        <app-button variant="secondary" type="button" (click)="showToast('warning')">Show warning</app-button>
      </div>
    </div>
    <div class="design-buttons__card">
      <p class="design-buttons__label">Info</p>
      <p class="design-buttons__hint">Use for low-priority updates.</p>
      <div class="design-buttons__row">
        <app-button variant="ghost" type="button" (click)="showToast('info')">Show info</app-button>
      </div>
    </div>
  </div>
</section>

<section a="location-selector" class="design-section design-location-selector">
  <h2 class="section-title">Location Selector</h2>
  <p class="section-description">
    Cascading dropdowns for selecting Indonesian administrative divisions with address line and ZIP code.
  </p>
  <div class="design-location-selector__card">
    <app-location-selector
      [value]="locationValue"
      (valueChange)="onLocationValueChange($event)"
      (addressChange)="onAddressChange($event)"></app-location-selector>
  </div>
</section>
